import { useQuery } from '@apollo/client';
import InputAdornment from '@mui/material/InputAdornment';
import * as Sentry from '@sentry/react';
import { FormEvent, useState } from 'react';

import { gengql } from '@/__generated__';
import { ResendCode, useResendCountdown } from '@/auth/components/shared';
import { codeSentMessage } from '@/auth/utils/codeDestination';
import {
  PHONE_FORMAT_MESSAGE,
  confirmPhone,
  enableSmsMfa,
  sendPhoneVerification,
} from '@/auth/utils/cognitoMfa';
import type { CognitoMfaType } from '@/auth/utils/cognitoSignIn';
import Button from '@/componentsv2/Button';
import InfoTip from '@/componentsv2/InfoTip';
import Input from '@/componentsv2/Input';
import { OTP_METHODS } from '@/core/api';
import { useApp } from '@/hooks/useAppContext';

import { type RunExclusive, mfaErrorMessage } from './mfaErrorMessage';

export const GET_PRIMARY_PHONE = gengql(/* GraphQL */ `
  query cognitoSmsEnrolmentPhones($userId: String!) {
    user(id: $userId) {
      id
      profile {
        id
        phones {
          address
          primary
          verified
        }
      }
    }
  }
`);

interface CognitoSmsEnrolmentProps {
  /** The signed-in user, whose primary phone is offered first. */
  userId: string;
  /** The methods on now, turned off when SMS goes on (ENG-6323). */
  turnOff?: CognitoMfaType[];
  onEnrolled: () => void;
  onCancel: () => void;
  /** Holds the settings page's change lock for each request. */
  runExclusive: RunExclusive;
}

// The api normalises any number it can parse, but the codes need the country.
const E164 = /^\+[1-9]\d{6,14}$/;

const DELIVERY_NOTE =
  'Texts to some countries outside the US can be delayed or filtered by carriers.';

function normalise(phone: string): string {
  return phone.replace(/[\s().-]/g, '');
}

/** The last two digits, the rest masked: •••23. */
function masked(phone: string): string {
  return `•••${phone.slice(-2)}`;
}

type Step = 'choose' | 'enter' | 'code' | 'confirmed';

/**
 * SMS factor (ENG-6107, ENG-6322). Cognito texts the sign-in codes to the
 * account's primary phone, which the api owns. SMS goes on only after a code
 * the api texts is confirmed, even for a phone confirmed before (ENG-6323);
 * the confirm also syncs the pool's phone_number.
 */
export default function CognitoSmsEnrolment({
  userId,
  turnOff,
  onEnrolled,
  onCancel,
  runExclusive,
}: CognitoSmsEnrolmentProps): JSX.Element {
  const { graphql } = useApp();
  // A failed read offers the number field, as for an account with no phone.
  const { data, loading } = useQuery(GET_PRIMARY_PHONE, {
    variables: { userId },
    fetchPolicy: 'network-only',
    onError: (err) => Sentry.captureException(err),
  });
  const primary = data?.user?.profile?.phones?.find((p) => p.primary);

  const [step, setStep] = useState<Step>();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  // Counts once a code is sent, not while the number is typed.
  const countdown = useResendCountdown(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await runExclusive(action);
    } catch (err) {
      setError(mfaErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const turnOn = () =>
    run(async () => {
      await enableSmsMfa(turnOff);
      onEnrolled();
    });

  async function textCode(number: string) {
    await sendPhoneVerification(graphql, number);
    countdown.restart();
    setPhone(number);
    setStep('code');
  }

  const sendCode = (number: string) => run(() => textCode(number));

  function handlePhoneSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const number = normalise(phone);
    if (!E164.test(number)) {
      setError(PHONE_FORMAT_MESSAGE);
      return;
    }
    void sendCode(number);
  }

  function handleTurnOnSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void turnOn();
  }

  function handleResend() {
    void run(async () => {
      await sendPhoneVerification(graphql, phone);
      countdown.restart();
      setCode('');
    });
  }

  function handleCodeSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void run(async () => {
      await confirmPhone(graphql, phone, code);
      // The code is spent: a failure from here on retries the turn-on alone.
      setStep('confirmed');
      await enableSmsMfa(turnOff);
      onEnrolled();
    });
  }

  const cancel = (
    <Button type="button" variant="tertiary" onClick={onCancel} disabled={busy}>
      Cancel
    </Button>
  );

  const errorLine = error && (
    <p className="text-14 text-[var(--errorText)]" role="alert">
      {error}
    </p>
  );

  if (loading) return <p className="text-on-surface-secondary">Loading…</p>;

  const current = step ?? (primary ? 'choose' : 'enter');

  if (current === 'confirmed') {
    return (
      <form className="flex flex-col gap-16" onSubmit={handleTurnOnSubmit}>
        <p className="text-on-surface">
          {masked(phone)} is confirmed. Turn on text message codes to finish.
        </p>
        {errorLine}
        <div className="flex justify-end gap-8">
          {cancel}
          <Button type="submit" disabled={busy}>
            Turn on
          </Button>
        </div>
      </form>
    );
  }

  if (current === 'code') {
    return (
      <form className="flex flex-col gap-16" onSubmit={handleCodeSubmit}>
        <p className="text-on-surface">
          {codeSentMessage(OTP_METHODS.SMS, phone)}. Enter it below.
        </p>
        <Input
          label="Verification Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={!!error}
          helperText={error}
          inputProps={{ autoComplete: 'one-time-code', inputMode: 'numeric' }}
        />
        <div className="flex flex-wrap items-center gap-8">
          <ResendCode secondsLeft={countdown.secondsLeft} onResend={handleResend} disabled={busy} />
          {/* Back to the number, to send the code somewhere else. */}
          <Button
            type="button"
            variant="tertiary"
            className="min-h-48 dark:!text-brand-primary"
            disabled={busy}
            onClick={() => {
              setCode('');
              setError(undefined);
              setStep('enter');
            }}
          >
            Change number
          </Button>
        </div>
        <div className="flex justify-end gap-8">
          {cancel}
          <Button type="submit" disabled={!code || busy}>
            Verify
          </Button>
        </div>
      </form>
    );
  }

  if (current === 'choose' && primary) {
    return (
      <div className="flex flex-col gap-16">
        <p className="text-on-surface">We will text a code to your phone each time you sign in.</p>
        {errorLine}
        {/* Stacked at the wider button's width; the dialog's X closes it, so no Cancel. */}
        <div className="ml-auto grid w-fit gap-8">
          <Button disabled={busy} onClick={() => void sendCode(primary.address)}>
            Send a code to {masked(primary.address)}
          </Button>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setError(undefined);
              setStep('enter');
            }}
          >
            Use a different number
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-16" onSubmit={handlePhoneSubmit}>
      <p className="text-on-surface">We will text a code to this number each time you sign in.</p>
      <Input
        label="Mobile Phone"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        // Locked while the code is on its way, so the code step names the number it went to.
        disabled={busy}
        error={!!error}
        helperText={error}
        inputProps={{ autoComplete: 'tel', inputMode: 'tel' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <InfoTip term="texts outside the US" definition={DELIVERY_NOTE} />
            </InputAdornment>
          ),
        }}
      />
      <div className="flex justify-end gap-8">
        {cancel}
        <Button type="submit" disabled={!phone || busy}>
          Continue
        </Button>
      </div>
    </form>
  );
}
